import type { AskRevFilter } from '@/types/askRevFilters';
import dayjs from 'dayjs';

/**
 * Safely evaluates simple arithmetic expressions (no eval)
 * Only supports basic arithmetic: +, -, *, /, and parentheses
 * @param expression The arithmetic expression to evaluate
 * @returns The evaluated numeric result
 */
function safeEvaluateArithmetic(expression: string): number {
	// Remove whitespace
	const cleaned = expression.replace(/\s+/g, '');

	// Basic validation - only allow numbers, operators, and parentheses
	if (!/^[0-9+\-*/()]*$/.test(cleaned)) {
		return 0;
	}

	try {
		// Simple recursive parser for arithmetic expressions
		return parseExpression(cleaned, 0).value;
	} catch {
		return 0;
	}
}

/**
 * Recursive parser for arithmetic expressions
 * @param expr The expression to parse
 * @param pos Current position in the expression
 * @returns Object with value and new position
 */
function parseExpression(
	expr: string,
	pos: number,
): { value: number; pos: number } {
	const result = parseTerm(expr, pos);

	while (result.pos < expr.length) {
		const op = expr[result.pos];
		if (op !== '+' && op !== '-') break;

		const nextTerm = parseTerm(expr, result.pos + 1);
		result.value =
			op === '+'
				? result.value + nextTerm.value
				: result.value - nextTerm.value;
		result.pos = nextTerm.pos;
	}

	return result;
}

/**
 * Parse multiplication and division terms
 */
function parseTerm(expr: string, pos: number): { value: number; pos: number } {
	const result = parseFactor(expr, pos);

	while (result.pos < expr.length) {
		const op = expr[result.pos];
		if (op !== '*' && op !== '/') break;

		const nextFactor = parseFactor(expr, result.pos + 1);
		result.value =
			op === '*'
				? result.value * nextFactor.value
				: result.value / nextFactor.value;
		result.pos = nextFactor.pos;
	}

	return result;
}

/**
 * Parse numbers and parenthesized expressions
 */
function parseFactor(
	expr: string,
	pos: number,
): { value: number; pos: number } {
	if (pos >= expr.length) return { value: 0, pos };

	if (expr[pos] === '(') {
		const result = parseExpression(expr, pos + 1);
		if (result.pos < expr.length && expr[result.pos] === ')') {
			result.pos++;
		}
		return result;
	}

	// Parse number (including negative numbers)
	let numStr = '';
	if (expr[pos] === '-' || expr[pos] === '+') {
		numStr += expr[pos++];
	}

	while (pos < expr.length && /\d/.test(expr[pos])) {
		numStr += expr[pos++];
	}

	return { value: Number.parseInt(numStr, 10) || 0, pos };
}

/**
 * Evaluates a date arithmetic expression containing functions like dayOfWeek()
 * @param expression The arithmetic expression to evaluate
 * @returns The evaluated numeric result
 */
function evaluateExpression(expression: string): number {
	// Replace dayOfWeek() with the current day of week (0 = Sunday, 1 = Monday, etc.)
	const dayOfWeekValue = dayjs().day();
	const withDayOfWeek = expression.replace(
		/dayOfWeek\(\)/g,
		dayOfWeekValue.toString(),
	);

	return safeEvaluateArithmetic(withDayOfWeek);
}

/**
 * Client-side variable replacement for AskRev filters
 * Replaces variable expressions like "today() + 2" with actual dates
 * @param value The filter value that may contain variable expressions
 * @returns The value with variables replaced with actual dates
 */
export function replaceVariables(
	value: string | number | boolean | null,
): string | number | boolean | null {
	if (typeof value !== 'string') {
		return value;
	}

	// Handle complex arithmetic expressions with today() (e.g., "today() + (7 - dayOfWeek() + 1)")
	const complexTodayMatch = value.match(/^today\(\)\s*([+\-])\s*\(([^)]+)\)$/);
	if (complexTodayMatch) {
		const [, operator, expression] = complexTodayMatch;
		const evaluatedDays = evaluateExpression(expression);
		const todayDate = dayjs();

		if (operator === '+') {
			return todayDate.add(evaluatedDays, 'day').format('YYYY-MM-DD');
		}
		return todayDate.subtract(evaluatedDays, 'day').format('YYYY-MM-DD');
	}

	// Handle arithmetic expressions with today() FIRST (before simple today() replacement)
	const todayArithmeticMatch = value.match(/^today\(\)\s*([+\-])\s*(\d+)$/);
	if (todayArithmeticMatch) {
		const [, operator, days] = todayArithmeticMatch;
		const numDays = Number.parseInt(days, 10);
		const todayDate = dayjs();

		if (operator === '+') {
			return todayDate.add(numDays, 'day').format('YYYY-MM-DD');
		}
		return todayDate.subtract(numDays, 'day').format('YYYY-MM-DD');
	}

	// Handle simple today() expression
	if (value === 'today()') {
		return dayjs().format('YYYY-MM-DD');
	}

	// Handle start of week/month/year expressions
	if (value === 'startOfWeek()') {
		return dayjs().startOf('week').format('YYYY-MM-DD');
	}

	if (value === 'startOfMonth()') {
		return dayjs().startOf('month').format('YYYY-MM-DD');
	}

	if (value === 'startOfYear()') {
		return dayjs().startOf('year').format('YYYY-MM-DD');
	}

	// Handle end of week/month/year expressions
	if (value === 'endOfWeek()') {
		return dayjs().endOf('week').format('YYYY-MM-DD');
	}

	if (value === 'endOfMonth()') {
		return dayjs().endOf('month').format('YYYY-MM-DD');
	}

	if (value === 'endOfYear()') {
		return dayjs().endOf('year').format('YYYY-MM-DD');
	}

	// Handle next day functions (nextMonday, nextTuesday, etc.)
	const nextDayMatch = value.match(
		/^next(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday)\(\)$/,
	);
	if (nextDayMatch) {
		const targetDay = nextDayMatch[1];
		const dayMap = {
			Sunday: 0,
			Monday: 1,
			Tuesday: 2,
			Wednesday: 3,
			Thursday: 4,
			Friday: 5,
			Saturday: 6,
		};

		const todayDate = dayjs();
		const currentDayOfWeek = todayDate.day();
		const targetDayOfWeek = dayMap[targetDay as keyof typeof dayMap];

		// Calculate days until next occurrence of the target day
		let daysUntilTarget = targetDayOfWeek - currentDayOfWeek;
		if (daysUntilTarget <= 0) {
			daysUntilTarget += 7; // Next week if today is the same day or later in the week
		}

		return todayDate.add(daysUntilTarget, 'day').format('YYYY-MM-DD');
	}

	// Return original value if no variables found
	return value;
}

/**
 * Replaces variables in an array of filters
 * @param filters Array of filters that may contain variable expressions
 * @returns New array of filters with variables replaced
 */
export function replaceVariablesInFilters(
	filters: AskRevFilter[],
): AskRevFilter[] {
	return filters.map((filter) => ({
		...filter,
		value: replaceVariables(filter.value),
	}));
}
